import { useEffect, useMemo, useRef } from "react";
import {
  buildVisualPool,
  type VisualEntry,
} from "../domain/visualPool";
import type { DrawRuntime, PickerEntry, ThemeId } from "../types";

interface CanvasStageProps {
  entries: PickerEntry[];
  runtime: DrawRuntime;
  theme: ThemeId;
  reducedMotion: boolean;
}

interface VisualToken {
  entry: VisualEntry;
  seed: number;
  x: number;
  y: number;
  depth: number;
  speed: number;
  phase: number;
}

interface CloudPlacement {
  token: VisualToken;
  x: number;
  y: number;
  fontSize: number;
  rotation: number;
  zoomAmount: number;
  zoomSpeed: number;
}

interface PlacementBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

interface Star {
  x: number;
  y: number;
  depth: number;
  speed: number;
}

interface WheelMotionState {
  angle: number;
  velocity: number;
  stopKey: number | null;
  stopStartAngle: number;
  stopTargetAngle: number;
}

const FULL_TURN = Math.PI * 2;

function hashText(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function seeded(seed: number, salt: number): number {
  let value = seed + Math.imul(salt + 1, 0x6d2b79f5);
  value = Math.imul(value ^ (value >>> 15), value | 1);
  value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
  return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function smoothstep(value: number): number {
  const progress = clamp01(value);
  return progress * progress * (3 - 2 * progress);
}

function easeOutQuint(value: number): number {
  return 1 - Math.pow(1 - clamp01(value), 5);
}

function positiveModulo(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}

const graphemeSegmenter = new Intl.Segmenter(undefined, {
  granularity: "grapheme",
});

function splitGraphemes(value: string): string[] {
  return Array.from(
    graphemeSegmenter.segment(value.replace(/\s+/g, "")),
    ({ segment }) => segment,
  );
}

function visualSample(entries: VisualEntry[], winner: PickerEntry | null): VisualEntry[] {
  if (entries.length <= 220) return entries;
  const stride = entries.length / 219;
  const sampled = Array.from({ length: 219 }, (_, index) => entries[Math.floor(index * stride)])
    .filter((entry): entry is VisualEntry => Boolean(entry));
  const winnerEntry = winner ? entries.find((entry) => entry.id === winner.id) : undefined;
  if (winnerEntry && !sampled.some((entry) => entry.id === winnerEntry.id)) {
    sampled.push(winnerEntry);
  }
  return sampled;
}

function wheelSample(entries: PickerEntry[], winner: PickerEntry | null): PickerEntry[] {
  const maximumSlices = 18;
  if (entries.length <= maximumSlices) return entries;

  const stride = entries.length / maximumSlices;
  const sampled = Array.from(
    { length: maximumSlices },
    (_, index) => entries[Math.floor(index * stride)],
  ).filter((entry): entry is PickerEntry => Boolean(entry));

  if (winner && !sampled.some((entry) => entry.id === winner.id)) {
    sampled[sampled.length - 1] = winner;
  }
  return sampled;
}

function makeTokens(entries: VisualEntry[]): VisualToken[] {
  return entries.map((entry, index) => {
    const seed = hashText(`${entry.id}:${entry.name}`);
    return {
      entry,
      seed,
      x: seeded(seed, 1) * 2 - 1,
      y: seeded(seed, 2) * 2 - 1,
      depth: 0.18 + seeded(seed, 3) * 1.35,
      speed: 0.45 + seeded(seed, 4) * 0.9,
      phase: seeded(seed, index + 5) * Math.PI * 2,
    };
  });
}

function makeStars(count: number): Star[] {
  return Array.from({ length: count }, (_, index) => {
    const seed = hashText(`star:${index}`);
    return {
      x: seeded(seed, 1) * 2 - 1,
      y: seeded(seed, 2) * 2 - 1,
      depth: 0.12 + seeded(seed, 3) * 1.5,
      speed: 0.35 + seeded(seed, 4),
    };
  });
}

function fitCanvas(canvas: HTMLCanvasElement, context: CanvasRenderingContext2D): {
  width: number;
  height: number;
} {
  const rectangle = canvas.getBoundingClientRect();
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  const width = Math.max(1, Math.floor(rectangle.width));
  const height = Math.max(1, Math.floor(rectangle.height));
  const pixelWidth = Math.floor(width * ratio);
  const pixelHeight = Math.floor(height * ratio);
  if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
    canvas.width = pixelWidth;
    canvas.height = pixelHeight;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
  }
  return { width, height };
}

function boundsOverlap(
  candidate: PlacementBounds,
  existing: PlacementBounds,
  padding: number,
): boolean {
  return !(
    candidate.right + padding < existing.left ||
    candidate.left - padding > existing.right ||
    candidate.bottom + padding < existing.top ||
    candidate.top - padding > existing.bottom
  );
}

function cloudFontSize(index: number, seed: number, scale: number): number {
  const variation = 0.88 + seeded(seed, 12) * 0.22;
  if (index < 3) return 62 * scale * variation;
  if (index < 8) return 44 * scale * variation;
  if (index < 18) return 31 * scale * variation;
  if (index < 42) return 21 * scale * variation;
  return (12 + seeded(seed, 13) * 5) * scale;
}

function makeCloudLayout(
  context: CanvasRenderingContext2D,
  tokens: VisualToken[],
  width: number,
  height: number,
): CloudPlacement[] {
  const scale = Math.max(0.58, Math.min(1.18, Math.min(width / 1_080, height / 720)));
  const centerX = width * 0.52;
  const centerY = height * 0.49;
  const horizontalMargin = Math.max(18, width * 0.035);
  const verticalMargin = Math.max(92, height * 0.13);
  const placedBounds: PlacementBounds[] = [];
  const placements: CloudPlacement[] = [];

  for (const [index, token] of tokens.entries()) {
    let fontSize = cloudFontSize(index, token.seed, scale);
    const vertical = index > 7 && seeded(token.seed, 14) < 0.18;
    const rotation = vertical ? Math.PI / 2 : 0;
    let accepted:
      | {
          x: number;
          y: number;
          bounds: PlacementBounds;
        }
      | undefined;

    for (let shrink = 0; shrink < 5 && !accepted; shrink += 1) {
      context.font = `700 ${fontSize}px Manrope, system-ui, sans-serif`;
      const measuredWidth = Math.min(width * 0.42, context.measureText(token.entry.name).width);
      const textWidth = vertical ? fontSize * 1.05 : measuredWidth;
      const textHeight = vertical ? measuredWidth : fontSize * 1.08;

      for (let attempt = 0; attempt < 1_600; attempt += 1) {
        const angle = token.phase + attempt * 2.399963;
        const radius = 4 + Math.sqrt(attempt) * 14.5 * scale;
        const x = centerX + Math.cos(angle) * radius * 1.08;
        const y = centerY + Math.sin(angle) * radius * 0.66;
        const bounds = {
          left: x - textWidth / 2,
          right: x + textWidth / 2,
          top: y - textHeight / 2,
          bottom: y + textHeight / 2,
        };
        if (
          bounds.left < horizontalMargin ||
          bounds.right > width - horizontalMargin ||
          bounds.top < verticalMargin ||
          bounds.bottom > height - verticalMargin
        ) {
          continue;
        }
        if (placedBounds.some((existing) => boundsOverlap(bounds, existing, 3 * scale))) {
          continue;
        }
        accepted = { x, y, bounds };
        break;
      }
      if (!accepted) fontSize *= 0.84;
    }

    if (!accepted) {
      const fallbackAngle = token.phase + index * 2.399963;
      const fallbackRadius = Math.min(width, height) * (0.12 + (index / tokens.length) * 0.4);
      const fallbackX = Math.max(
        horizontalMargin,
        Math.min(width - horizontalMargin, centerX + Math.cos(fallbackAngle) * fallbackRadius),
      );
      const fallbackY = Math.max(
        verticalMargin,
        Math.min(
          height - verticalMargin,
          centerY + Math.sin(fallbackAngle) * fallbackRadius * 0.68,
        ),
      );
      accepted = {
        x: fallbackX,
        y: fallbackY,
        bounds: {
          left: fallbackX - fontSize * 2,
          right: fallbackX + fontSize * 2,
          top: fallbackY - fontSize,
          bottom: fallbackY + fontSize,
        },
      };
    }

    placedBounds.push(accepted.bounds);
    placements.push({
      token,
      x: accepted.x,
      y: accepted.y,
      fontSize,
      rotation,
      zoomAmount: 0.08 + seeded(token.seed, 15) * (index < 18 ? 0.16 : 0.3),
      zoomSpeed: 1.1 + seeded(token.seed, 16) * 0.9,
    });
  }

  return placements;
}

function drawWinner(
  context: CanvasRenderingContext2D,
  winner: PickerEntry,
  width: number,
  height: number,
  progress: number,
  palette: { accent: string; text: string },
): void {
  const eased = 1 - Math.pow(1 - Math.min(1, progress), 3);
  const fontSize = Math.max(34, Math.min(82, width * 0.075)) * (0.72 + eased * 0.28);
  const fade = Math.min(1, progress * 1.6);
  context.save();
  context.translate(width / 2, height / 2);
  context.globalAlpha = fade;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.shadowColor = palette.accent;
  context.shadowBlur = 28 * eased;
  context.font = `720 ${fontSize}px Manrope, sans-serif`;
  context.fillStyle = palette.text;
  const maxWidth = width * 0.78;
  context.fillText(winner.name, 0, 0, maxWidth);
  if (winner.category) {
    context.shadowBlur = 0;
    context.font = `600 ${Math.max(12, Math.min(16, width * 0.018))}px "JetBrains Mono", monospace`;
    context.fillStyle = palette.accent;
    context.fillText(winner.category.toLocaleUpperCase(), 0, fontSize * 0.78, maxWidth);
  }
  context.restore();
}

function drawStarVoyage(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  tokens: VisualToken[],
  stars: Star[],
  runtime: DrawRuntime,
  now: number,
  delta: number,
  reducedMotion: boolean,
): void {
  const gradient = context.createRadialGradient(
    width * 0.48,
    height * 0.45,
    0,
    width * 0.48,
    height * 0.45,
    Math.max(width, height) * 0.72,
  );
  gradient.addColorStop(0, "#18212a");
  gradient.addColorStop(0.42, "#0c1117");
  gradient.addColorStop(1, "#07090c");
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);

  const moving = runtime.status === "running";
  const stopping = runtime.status === "stopping";
  const motionSpeed = reducedMotion ? 0 : moving ? 1 : stopping ? 0.28 : 0.16;
  const centerX = width * 0.48;
  const centerY = height * 0.46;

  context.lineCap = "round";
  for (const star of stars) {
    if (motionSpeed > 0) {
      star.depth -= (delta / 1000) * star.speed * 0.48 * motionSpeed;
      if (star.depth < 0.08) star.depth = 1.55;
    }
    const scale = 1 / star.depth;
    const x = centerX + star.x * width * 0.38 * scale;
    const y = centerY + star.y * height * 0.36 * scale;
    if (x < 0 || x > width || y < 0 || y > height) continue;
    const opacity = Math.min(0.9, Math.max(0.08, (1.6 - star.depth) * 0.55));
    context.strokeStyle = `rgba(238, 236, 226, ${opacity})`;
    context.lineWidth = Math.min(2.2, 0.45 * scale);
    context.beginPath();
    context.moveTo(x, y);
    const tail = motionSpeed * 6 * scale;
    context.lineTo(x - star.x * tail, y - star.y * tail);
    context.stroke();
  }

  const revealProgress =
    runtime.stopRequestedAt === null ? 0 : Math.min(1, (now - runtime.stopRequestedAt) / 1_100);
  const tokenOpacity = runtime.status === "stopping" ? 1 - revealProgress : runtime.status === "revealed" ? 0.1 : 1;

  for (const token of tokens) {
    if (motionSpeed > 0) {
      token.depth -= (delta / 1000) * token.speed * 0.3 * motionSpeed;
      if (token.depth < 0.16) token.depth = 1.5;
    }
    const scale = 1 / token.depth;
    const x = centerX + token.x * width * 0.31 * scale;
    const y = centerY + token.y * height * 0.27 * scale;
    if (x < -120 || x > width + 120 || y < -60 || y > height + 60) continue;
    const fontSize = Math.max(10, Math.min(26, 10 * scale));
    context.globalAlpha =
      tokenOpacity * Math.min(0.72, Math.max(0.06, (1.55 - token.depth) * 0.55));
    context.font = `600 ${fontSize}px Manrope, sans-serif`;
    context.textAlign = "center";
    context.fillStyle = "#d8d8d1";
    context.fillText(token.entry.name, x, y, 240);
  }
  context.globalAlpha = 1;

  if (runtime.winner && (runtime.status === "stopping" || runtime.status === "revealed")) {
    drawWinner(context, runtime.winner, width, height, runtime.status === "revealed" ? 1 : revealProgress, {
      accent: "#e9785b",
      text: "#f4f1e9",
    });
  }
}

function drawCodeRain(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  tokens: VisualToken[],
  runtime: DrawRuntime,
  now: number,
  delta: number,
  reducedMotion: boolean,
): void {
  context.fillStyle = reducedMotion ? "#030906" : "rgba(1, 8, 4, 0.19)";
  context.fillRect(0, 0, width, height);
  const activeDraw =
    runtime.status === "running" || runtime.status === "stopping";
  const launchElapsed =
    runtime.startedAt === null ? Number.POSITIVE_INFINITY : Math.max(0, now - runtime.startedAt);
  const previewExitDuration = 760;
  const smallStreamDelay = 650;
  const smallStreamEntryDuration = 550;
  const previewExitProgress = smoothstep(launchElapsed / previewExitDuration);
  const smallStreamEntryProgress = smoothstep(
    (launchElapsed - smallStreamDelay) / smallStreamEntryDuration,
  );
  const matrixActive =
    activeDraw &&
    (reducedMotion || runtime.status !== "running" || launchElapsed >= smallStreamDelay);
  const transitionOpacity =
    !activeDraw || reducedMotion || runtime.status !== "running"
      ? 1
      : matrixActive
        ? smallStreamEntryProgress
        : 1 - previewExitProgress;
  const previewExitDrop =
    activeDraw && !matrixActive && !reducedMotion
      ? previewExitProgress * height * 1.15
      : 0;
  const actualTokens = tokens.filter((token) => !token.entry.isDummy);
  const previewTokens = actualTokens.length > 0 ? actualTokens : tokens;
  const visibleTokens = matrixActive ? tokens : previewTokens;
  const launchSpeedMultiplier = 2;
  const previewRainSpeed = 0.27;
  const previewNamePassesPerSecond = 0.14;
  const rainSpeed =
    runtime.status === "running"
      ? matrixActive
        ? previewRainSpeed * launchSpeedMultiplier
        : previewRainSpeed
      : runtime.status === "stopping"
        ? 0.32
        : runtime.status === "revealed"
          ? 0.04
          : previewRainSpeed;
  const namePassesPerSecond =
    runtime.status === "running"
      ? matrixActive
        ? previewNamePassesPerSecond * launchSpeedMultiplier
        : previewNamePassesPerSecond
      : runtime.status === "stopping"
        ? 0.24
        : runtime.status === "revealed"
          ? 0.03
          : previewNamePassesPerSecond;
  const revealProgress =
    runtime.stopRequestedAt === null ? 0 : Math.min(1, (now - runtime.stopRequestedAt) / 1_000);
  const fade = runtime.status === "stopping" ? 1 - revealProgress : runtime.status === "revealed" ? 0.12 : 1;
  const backgroundColumnCount = matrixActive
    ? Math.min(tokens.length, Math.max(28, Math.floor(width / 18)))
    : Math.min(
        visibleTokens.length * 3,
        Math.max(14, Math.floor(width / 42)),
      );
  const backgroundColumnSpacing = width / Math.max(backgroundColumnCount, 1);
  const scrambleInterval = matrixActive ? 240 / launchSpeedMultiplier : 240;

  context.textAlign = "center";
  context.textBaseline = "middle";
  for (let column = 0; column < backgroundColumnCount; column += 1) {
    const token =
      visibleTokens[(column * 37) % Math.max(visibleTokens.length, 1)];
    if (!token) continue;
    const fontSize = matrixActive
      ? 10 + seeded(token.seed, 20) * 4.5
      : 12 + seeded(token.seed, 20) * 5;
    const characterStep = fontSize * (1.12 + seeded(token.seed, 21) * 0.18);
    const nameCharacters = splitGraphemes(token.entry.name);
    const streamCharacters =
      nameCharacters.length >= 3
        ? [...nameCharacters, "0", "1"]
        : [...nameCharacters, "0", "1", "7", "9"];
    const trailLength = matrixActive
      ? 14 + Math.floor(seeded(token.seed, 22) * 16)
      : 10 + Math.floor(seeded(token.seed, 22) * 10);
    const pixelsPerSecond = 230 + token.speed * 250;
    const jitter =
      (seeded(token.seed, 23) - 0.5) *
      Math.min(10, backgroundColumnSpacing * 0.62);
    const x = (column + 0.5) * backgroundColumnSpacing + jitter;

    if (!reducedMotion) {
      token.y += (delta / 1000) * (pixelsPerSecond / height) * rainSpeed;
      const resetPoint = 1 + (trailLength * characterStep) / height;
      if (token.y > resetPoint) {
        const resetInterval = matrixActive ? 6_000 / launchSpeedMultiplier : 6_000;
        token.y =
          -(0.08 + seeded(token.seed, Math.floor(now / resetInterval)) * 0.72);
      }
    }

    const headY = token.y * height + previewExitDrop;
    const scrambleTick = Math.floor(now / scrambleInterval);
    context.font = `590 ${fontSize}px "JetBrains Mono", ui-monospace, monospace`;

    for (let trailIndex = trailLength - 1; trailIndex >= 0; trailIndex -= 1) {
      const y = headY - trailIndex * characterStep;
      if (y < -characterStep || y > height + characterStep) continue;
      const progress = 1 - trailIndex / trailLength;
      const characterIndex = Math.floor(
        seeded(
          token.seed + column * 17 + trailIndex * 31,
          scrambleTick,
        ) * streamCharacters.length,
      );
      const character = streamCharacters[characterIndex] ?? "0";
      const opacity =
        fade *
        transitionOpacity *
        Math.pow(progress, 1.65) *
        (0.32 + seeded(token.seed, 24) * 0.48);

      context.globalAlpha = Math.min(1, opacity);
      if (trailIndex === 0) {
        context.fillStyle = "#dbffe4";
        context.shadowColor = "#72ff91";
        context.shadowBlur = 12;
      } else if (trailIndex < 3) {
        context.fillStyle = "#7fff9a";
        context.shadowColor = "#42dd68";
        context.shadowBlur = 5;
      } else {
        context.fillStyle = trailIndex < trailLength * 0.5 ? "#37b95a" : "#17692f";
        context.shadowBlur = 0;
      }
      context.fillText(character, x, y);
    }
  }

  const readableLaneCount = matrixActive
    ? Math.max(14, Math.min(24, Math.floor(width / 38)))
    : Math.max(
        1,
        Math.min(
          previewTokens.length,
          Math.max(8, Math.floor(width / 84)),
        ),
      );
  const readableLaneSpacing = width / readableLaneCount;
  const readableFontSize = matrixActive
    ? Math.max(10, Math.min(14.5, readableLaneSpacing * 0.4))
    : Math.max(19, Math.min(28, readableLaneSpacing * 0.32));
  const readableStep = readableFontSize * (matrixActive ? 1.12 : 1.16);
  const elapsed = now / 1_000;
  const readableTokens = matrixActive ? tokens : previewTokens;

  for (let lane = 0; lane < readableLaneCount; lane += 1) {
    const lanePhase = reducedMotion
      ? seeded(lane + 1, 51)
      : elapsed * namePassesPerSecond + seeded(lane + 1, 53);
    const passIndex = Math.floor(lanePhase);
    const passProgress = lanePhase - passIndex;
    const tokenIndex = matrixActive
      ? ((passIndex * readableLaneCount + lane) % readableTokens.length +
          readableTokens.length) %
        readableTokens.length
      : lane % readableTokens.length;
    const token = readableTokens[tokenIndex];
    if (!token) continue;

    const characters = splitGraphemes(token.entry.name);
    const nameHeight = Math.max(readableStep, (characters.length - 1) * readableStep);
    const activeTravel = height + nameHeight + 140;
    const previewTravel = height + nameHeight + 180;
    const previewPassProgress = reducedMotion
      ? seeded(token.seed, 54)
      : (seeded(token.seed, 54) +
          elapsed *
            previewNamePassesPerSecond *
            (0.82 + seeded(token.seed, 55) * 0.36)) %
        1;
    const startY = matrixActive
      ? -nameHeight - 70 + passProgress * activeTravel
      : -nameHeight - 90 + previewPassProgress * previewTravel + previewExitDrop;
    const laneJitter = (seeded(token.seed, 52) - 0.5) * readableLaneSpacing * 0.24;
    const x = (lane + 0.5) * readableLaneSpacing + laneJitter;

    context.font = `720 ${readableFontSize}px "JetBrains Mono", ui-monospace, monospace`;
    for (const [characterIndex, character] of characters.entries()) {
      const y = startY + characterIndex * readableStep;
      if (y < -readableStep || y > height + readableStep) continue;

      const leadingEdge = characterIndex === characters.length - 1;
      context.globalAlpha = fade * transitionOpacity * (leadingEdge ? 0.98 : 0.78);
      context.fillStyle = leadingEdge ? "#e7ffec" : "#82f29a";
      context.shadowColor = leadingEdge ? "#82ff9e" : "#37d55f";
      context.shadowBlur = leadingEdge ? 15 : 5;
      context.fillText(character, x, y);
    }
  }

  context.shadowBlur = 0;
  context.globalAlpha = 1;

  const focusGlow = context.createRadialGradient(
    width * 0.5,
    height * 0.5,
    0,
    width * 0.5,
    height * 0.5,
    Math.min(width, height) * 0.34,
  );
  focusGlow.addColorStop(0, "rgba(79, 255, 119, 0.045)");
  focusGlow.addColorStop(1, "rgba(79, 255, 119, 0)");
  context.fillStyle = focusGlow;
  context.fillRect(0, 0, width, height);

  if (runtime.winner && (runtime.status === "stopping" || runtime.status === "revealed")) {
    drawWinner(context, runtime.winner, width, height, runtime.status === "revealed" ? 1 : revealProgress, {
      accent: "#77c091",
      text: "#eff8f1",
    });
  }
}

function drawKineticCloud(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  placements: CloudPlacement[],
  runtime: DrawRuntime,
  now: number,
  reducedMotion: boolean,
): void {
  const gradient = context.createRadialGradient(
    width * 0.52,
    height * 0.48,
    20,
    width * 0.52,
    height * 0.48,
    Math.max(width, height) * 0.7,
  );
  gradient.addColorStop(0, "#2a1d1a");
  gradient.addColorStop(0.38, "#151314");
  gradient.addColorStop(1, "#090a0c");
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);

  const revealProgress =
    runtime.stopRequestedAt === null ? 0 : Math.min(1, (now - runtime.stopRequestedAt) / 1_050);
  const fade = runtime.status === "stopping" ? 1 - revealProgress : runtime.status === "revealed" ? 0.08 : 1;
  const motionAmplitude = reducedMotion
    ? 0
    : runtime.status === "running"
      ? 1
      : runtime.status === "stopping"
        ? 0.28
        : 0.56;
  const motionTempo = reducedMotion
    ? 0
    : runtime.status === "running"
      ? 2.6
      : runtime.status === "stopping"
        ? 0.65
        : 1.35;
  const relocationAmount = reducedMotion
    ? 0
    : runtime.status === "running"
      ? 1
      : runtime.status === "stopping"
        ? 0.18
        : 0.84;
  const time = now / 1000;

  for (let index = placements.length - 1; index >= 0; index -= 1) {
    const placement = placements[index];
    if (!placement) continue;
    const { token } = placement;
    const breathingPhase = time * placement.zoomSpeed * motionTempo + token.phase;
    const phaseInCycles = breathingPhase / (Math.PI * 2);
    const cycleIndex = Math.floor(phaseInCycles);
    const cycleProgress = phaseInCycles - cycleIndex;
    const animatedZoomAmount = placement.zoomAmount * motionAmplitude;
    const minimumZoom = Math.max(0.08, 1 - animatedZoomAmount * 3.2);
    let lifecycleOpacity = 1;
    let zoom = 1;

    if (!reducedMotion) {
      if (cycleProgress < 0.5) {
        const zoomInProgress = smoothstep(cycleProgress / 0.2);
        const zoomOutProgress = smoothstep((cycleProgress - 0.2) / 0.2);
        lifecycleOpacity = 1 - smoothstep((cycleProgress - 0.22) / 0.18);
        zoom =
          1 +
          animatedZoomAmount * zoomInProgress -
          animatedZoomAmount * 3.2 * zoomOutProgress;
      } else {
        const fadeInProgress = smoothstep((cycleProgress - 0.62) / 0.2);
        const settleProgress = smoothstep((cycleProgress - 0.82) / 0.18);
        lifecycleOpacity = fadeInProgress;
        zoom =
          minimumZoom +
          (1 + animatedZoomAmount * 0.9 - minimumZoom) * fadeInProgress -
          animatedZoomAmount * 0.9 * settleProgress;
      }
    }

    const originX = placement.x;
    const originY = placement.y;
    const resolveCycleLocation = (locationCycle: number) => {
      if (locationCycle % 2 === 0 || reducedMotion) {
        return { x: originX, y: originY };
      }
      const destinationX =
        width * (0.2 + seeded(token.seed, 70 + locationCycle * 2) * 0.6);
      const destinationY =
        height * (0.18 + seeded(token.seed, 71 + locationCycle * 2) * 0.64);
      return {
        x: originX + (destinationX - originX) * relocationAmount,
        y: originY + (destinationY - originY) * relocationAmount,
      };
    };
    const currentLocation = resolveCycleLocation(cycleIndex);
    const nextLocation = resolveCycleLocation(cycleIndex + 1);
    const renderLocation = cycleProgress < 0.5 ? currentLocation : nextLocation;
    const prominence = 1 - Math.min(1, index / Math.max(1, placements.length - 1));
    const opacity =
      fade *
      lifecycleOpacity *
      Math.min(0.94, 0.36 + prominence * 0.46 + Math.max(0, zoom - 1) * 0.3);

    context.save();
    context.translate(renderLocation.x, renderLocation.y);
    context.rotate(placement.rotation);
    context.scale(zoom, zoom);
    context.globalAlpha = opacity;
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.font = `700 ${placement.fontSize}px Manrope, system-ui, sans-serif`;
    context.fillStyle = seeded(token.seed, 25) > 0.88 ? "#e9785b" : "#e8e3da";
    if (zoom > 1.08) {
      context.shadowColor = "rgba(233, 120, 91, 0.42)";
      context.shadowBlur = 12 + (zoom - 1) * 24;
    }
    context.fillText(token.entry.name, 0, 0, width * 0.42);
    context.restore();
  }

  if (runtime.winner && (runtime.status === "stopping" || runtime.status === "revealed")) {
    drawWinner(context, runtime.winner, width, height, runtime.status === "revealed" ? 1 : revealProgress, {
      accent: "#e9785b",
      text: "#f5eee7",
    });
  }
}

function drawImmersiveWheel(
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  entries: PickerEntry[],
  runtime: DrawRuntime,
  wheelMotion: WheelMotionState,
  now: number,
  delta: number,
  reducedMotion: boolean,
): void {
  const compact = width < 980;
  const centerX = width * (compact ? 0.5 : 0.66);
  const centerY = height * (compact ? 0.59 : 0.5);
  const radius = Math.max(
    86,
    Math.min(
      height * (compact ? 0.275 : 0.365),
      width * (compact ? 0.34 : 0.255),
    ),
  );

  const background = context.createRadialGradient(
    centerX,
    centerY,
    radius * 0.05,
    centerX,
    centerY,
    Math.max(width, height) * 0.72,
  );
  background.addColorStop(0, "#28201d");
  background.addColorStop(0.24, "#151416");
  background.addColorStop(0.58, "#0b0d10");
  background.addColorStop(1, "#06080b");
  context.fillStyle = background;
  context.fillRect(0, 0, width, height);

  const leftVignette = context.createLinearGradient(0, 0, width * 0.62, 0);
  leftVignette.addColorStop(0, "rgba(4, 6, 9, 0.84)");
  leftVignette.addColorStop(0.72, "rgba(4, 6, 9, 0.16)");
  leftVignette.addColorStop(1, "rgba(4, 6, 9, 0)");
  context.fillStyle = leftVignette;
  context.fillRect(0, 0, width, height);

  const winnerIndex = runtime.winner
    ? entries.findIndex((entry) => entry.id === runtime.winner?.id)
    : -1;
  const sliceAngle = FULL_TURN / Math.max(entries.length, 1);
  const pointerAngle = -Math.PI / 2;

  if (
    (runtime.status === "stopping" || runtime.status === "revealed") &&
    runtime.stopRequestedAt !== null &&
    wheelMotion.stopKey !== runtime.stopRequestedAt
  ) {
    const selectedIndex = winnerIndex >= 0 ? winnerIndex : 0;
    const desiredAngle = pointerAngle - (selectedIndex + 0.5) * sliceAngle;
    const landingDelta = positiveModulo(desiredAngle - wheelMotion.angle, FULL_TURN);
    wheelMotion.stopKey = runtime.stopRequestedAt;
    wheelMotion.stopStartAngle = wheelMotion.angle;
    wheelMotion.stopTargetAngle =
      wheelMotion.angle + landingDelta + (reducedMotion ? 0 : FULL_TURN * 2);
  }

  if (
    (runtime.status === "stopping" || runtime.status === "revealed") &&
    runtime.stopRequestedAt !== null
  ) {
    const stopProgress =
      runtime.status === "revealed"
        ? 1
        : easeOutQuint((now - runtime.stopRequestedAt) / 1_080);
    wheelMotion.angle =
      wheelMotion.stopStartAngle +
      (wheelMotion.stopTargetAngle - wheelMotion.stopStartAngle) * stopProgress;
    wheelMotion.velocity = 0;
  } else {
    wheelMotion.stopKey = null;
    const targetVelocity =
      reducedMotion ? 0 : runtime.status === "running" ? 0.0105 : 0.00012;
    const responsiveness = runtime.status === "running" ? 0.055 : 0.025;
    wheelMotion.velocity +=
      (targetVelocity - wheelMotion.velocity) *
      Math.min(1, delta * responsiveness);
    wheelMotion.angle += wheelMotion.velocity * delta;
  }

  context.save();
  context.translate(centerX, centerY);

  const orbitTime = reducedMotion ? 0 : now * 0.00008;
  context.save();
  context.rotate(-0.16);
  context.strokeStyle = "rgba(233, 120, 91, 0.15)";
  context.lineWidth = 1;
  context.setLineDash([3, 11]);
  context.beginPath();
  context.ellipse(0, 0, radius * 1.42, radius * 1.12, 0, 0, FULL_TURN);
  context.stroke();
  context.setLineDash([]);
  context.restore();

  for (let particleIndex = 0; particleIndex < 26; particleIndex += 1) {
    const seed = hashText(`wheel-particle:${particleIndex}`);
    const orbitRadius =
      radius * (1.04 + seeded(seed, 1) * 0.44);
    const angle =
      seeded(seed, 2) * FULL_TURN +
      orbitTime * (0.35 + seeded(seed, 3) * 1.25);
    const x = Math.cos(angle) * orbitRadius;
    const y = Math.sin(angle) * orbitRadius * (0.78 + seeded(seed, 4) * 0.24);
    const size = 0.7 + seeded(seed, 5) * 1.8;
    context.globalAlpha = 0.16 + seeded(seed, 6) * 0.52;
    context.fillStyle = seeded(seed, 7) > 0.72 ? "#e9785b" : "#d9d2c4";
    context.beginPath();
    context.arc(x, y, size, 0, FULL_TURN);
    context.fill();
  }
  context.globalAlpha = 1;

  const halo = context.createRadialGradient(0, 0, radius * 0.72, 0, 0, radius * 1.22);
  halo.addColorStop(0, "rgba(233, 120, 91, 0)");
  halo.addColorStop(0.68, "rgba(233, 120, 91, 0.06)");
  halo.addColorStop(0.86, "rgba(233, 120, 91, 0.18)");
  halo.addColorStop(1, "rgba(233, 120, 91, 0)");
  context.fillStyle = halo;
  context.beginPath();
  context.arc(0, 0, radius * 1.24, 0, FULL_TURN);
  context.fill();

  context.shadowColor = "rgba(0, 0, 0, 0.82)";
  context.shadowBlur = radius * 0.12;
  context.shadowOffsetY = radius * 0.04;
  const outerRim = context.createRadialGradient(
    -radius * 0.28,
    -radius * 0.35,
    radius * 0.12,
    0,
    0,
    radius * 1.05,
  );
  outerRim.addColorStop(0, "#eee5d5");
  outerRim.addColorStop(0.48, "#77736d");
  outerRim.addColorStop(0.78, "#292c2e");
  outerRim.addColorStop(1, "#0b0d0f");
  context.fillStyle = outerRim;
  context.beginPath();
  context.arc(0, 0, radius * 1.055, 0, FULL_TURN);
  context.fill();
  context.shadowBlur = 0;
  context.shadowOffsetY = 0;

  context.fillStyle = "#111417";
  context.beginPath();
  context.arc(0, 0, radius * 1.005, 0, FULL_TURN);
  context.fill();

  const segmentColors = ["#df755a", "#e6ddcd", "#1d2022", "#806a35", "#a54e3d", "#d4cbbd"];
  const segmentTextColors = ["#17191a", "#202225", "#f1ebe1", "#f5eee4", "#f4e9df", "#202225"];
  context.save();
  context.rotate(wheelMotion.angle);

  if (entries.length === 0) {
    const emptyGradient = context.createRadialGradient(0, 0, 0, 0, 0, radius);
    emptyGradient.addColorStop(0, "#242629");
    emptyGradient.addColorStop(1, "#101215");
    context.fillStyle = emptyGradient;
    context.beginPath();
    context.arc(0, 0, radius * 0.97, 0, FULL_TURN);
    context.fill();
  } else {
    for (const [index, entry] of entries.entries()) {
      const startAngle = index * sliceAngle;
      const endAngle = startAngle + sliceAngle;
      const middleAngle = startAngle + sliceAngle / 2;
      const colorIndex = index % segmentColors.length;

      context.beginPath();
      context.moveTo(0, 0);
      context.arc(0, 0, radius * 0.97, startAngle, endAngle);
      context.closePath();
      context.fillStyle = segmentColors[colorIndex] ?? "#1d2022";
      context.fill();
      context.strokeStyle = "rgba(6, 8, 10, 0.55)";
      context.lineWidth = Math.max(1, radius * 0.006);
      context.stroke();

      const worldAngle = wheelMotion.angle + middleAngle;
      context.save();
      context.rotate(middleAngle);
      context.translate(radius * 0.61, 0);
      if (Math.cos(worldAngle) < 0) context.rotate(Math.PI);
      context.fillStyle = segmentTextColors[colorIndex] ?? "#f1ebe1";
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.font = `760 ${Math.max(
        10,
        Math.min(17, radius * (entries.length > 14 ? 0.038 : 0.048)),
      )}px Manrope, system-ui, sans-serif`;
      context.fillText(entry.name, 0, 0, radius * 0.58);
      context.restore();
    }
  }
  context.restore();

  const wheelSheen = context.createLinearGradient(
    -radius * 0.65,
    -radius,
    radius * 0.45,
    radius,
  );
  wheelSheen.addColorStop(0, "rgba(255, 255, 255, 0.16)");
  wheelSheen.addColorStop(0.34, "rgba(255, 255, 255, 0.015)");
  wheelSheen.addColorStop(0.68, "rgba(0, 0, 0, 0.08)");
  wheelSheen.addColorStop(1, "rgba(0, 0, 0, 0.24)");
  context.fillStyle = wheelSheen;
  context.beginPath();
  context.arc(0, 0, radius * 0.965, 0, FULL_TURN);
  context.fill();

  context.strokeStyle = "rgba(240, 230, 213, 0.48)";
  context.lineWidth = Math.max(1, radius * 0.008);
  context.beginPath();
  context.arc(0, 0, radius * 0.985, 0, FULL_TURN);
  context.stroke();

  for (let tick = 0; tick < Math.max(36, entries.length * 3); tick += 1) {
    const tickAngle = (tick / Math.max(36, entries.length * 3)) * FULL_TURN;
    const inner = radius * (tick % 3 === 0 ? 1.005 : 1.018);
    const outer = radius * 1.038;
    context.strokeStyle =
      tick % 3 === 0 ? "rgba(239, 229, 210, 0.72)" : "rgba(239, 229, 210, 0.28)";
    context.lineWidth = tick % 3 === 0 ? 1.5 : 1;
    context.beginPath();
    context.moveTo(Math.cos(tickAngle) * inner, Math.sin(tickAngle) * inner);
    context.lineTo(Math.cos(tickAngle) * outer, Math.sin(tickAngle) * outer);
    context.stroke();
  }

  const hubOuter = context.createRadialGradient(
    -radius * 0.045,
    -radius * 0.06,
    0,
    0,
    0,
    radius * 0.19,
  );
  hubOuter.addColorStop(0, "#fff0dd");
  hubOuter.addColorStop(0.22, "#e78667");
  hubOuter.addColorStop(0.56, "#8f4539");
  hubOuter.addColorStop(0.76, "#292b2d");
  hubOuter.addColorStop(1, "#0b0d0f");
  context.shadowColor = "rgba(233, 120, 91, 0.55)";
  context.shadowBlur = radius * 0.1;
  context.fillStyle = hubOuter;
  context.beginPath();
  context.arc(0, 0, radius * 0.185, 0, FULL_TURN);
  context.fill();
  context.shadowBlur = 0;

  context.strokeStyle = "rgba(255, 234, 211, 0.5)";
  context.lineWidth = Math.max(1, radius * 0.006);
  context.beginPath();
  context.arc(0, 0, radius * 0.115, 0, FULL_TURN);
  context.stroke();
  context.fillStyle = "#121416";
  context.beginPath();
  context.arc(0, 0, radius * 0.055, 0, FULL_TURN);
  context.fill();
  context.fillStyle = "#f0a084";
  context.beginPath();
  context.arc(0, 0, radius * 0.018, 0, FULL_TURN);
  context.fill();

  const pointerWidth = radius * 0.115;
  const pointerTop = -radius * 1.13;
  const pointerTip = -radius * 0.91;
  context.shadowColor = "rgba(0, 0, 0, 0.78)";
  context.shadowBlur = radius * 0.045;
  context.shadowOffsetY = radius * 0.018;
  context.fillStyle = "#ee7b5d";
  context.beginPath();
  context.moveTo(-pointerWidth, pointerTop);
  context.lineTo(pointerWidth, pointerTop);
  context.lineTo(0, pointerTip);
  context.closePath();
  context.fill();
  context.shadowBlur = 0;
  context.shadowOffsetY = 0;
  context.strokeStyle = "rgba(255, 224, 210, 0.72)";
  context.lineWidth = Math.max(1, radius * 0.006);
  context.stroke();

  context.restore();

  const revealProgress =
    runtime.stopRequestedAt === null ? 0 : clamp01((now - runtime.stopRequestedAt) / 1_100);
  const winnerProgress =
    runtime.status === "revealed" ? 1 : smoothstep((revealProgress - 0.7) / 0.3);
  if (
    runtime.winner &&
    (runtime.status === "stopping" || runtime.status === "revealed") &&
    winnerProgress > 0
  ) {
    const veil = context.createRadialGradient(
      width * 0.5,
      height * 0.5,
      0,
      width * 0.5,
      height * 0.5,
      Math.max(width, height) * 0.52,
    );
    veil.addColorStop(0, `rgba(5, 7, 9, ${0.68 * winnerProgress})`);
    veil.addColorStop(1, `rgba(5, 7, 9, ${0.16 * winnerProgress})`);
    context.fillStyle = veil;
    context.fillRect(0, 0, width, height);
    drawWinner(context, runtime.winner, width, height, winnerProgress, {
      accent: "#e9785b",
      text: "#f5efe5",
    });
  }
}

export function CanvasStage({
  entries,
  runtime,
  theme,
  reducedMotion,
}: CanvasStageProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const runtimeRef = useRef(runtime);
  const visualEntries = useMemo(() => buildVisualPool(entries), [entries]);
  const sampledEntries = useMemo(
    () => visualSample(visualEntries, runtime.winner),
    [runtime.winner, visualEntries],
  );
  const wheelEntries = useMemo(
    () => wheelSample(entries, runtime.winner),
    [entries, runtime.winner],
  );
  const sampleKey = sampledEntries.map((entry) => entry.id).join(":");
  const wheelKey = wheelEntries.map((entry) => entry.id).join(":");

  useEffect(() => {
    runtimeRef.current = runtime;
  }, [runtime]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d", { alpha: false });
    if (!context) return;

    const tokens = makeTokens(sampledEntries);
    const stars = makeStars(240);
    const wheelSeed = hashText(wheelKey || "pickscape-wheel");
    const wheelMotion: WheelMotionState = {
      angle: seeded(wheelSeed, 91) * FULL_TURN,
      velocity: 0,
      stopKey: null,
      stopStartAngle: 0,
      stopTargetAngle: 0,
    };
    let cloudLayout: CloudPlacement[] = [];
    let cloudLayoutWidth = 0;
    let cloudLayoutHeight = 0;
    let animationFrame = 0;
    let previous = performance.now();
    let disposed = false;
    const resizeObserver = new ResizeObserver(() => {
      fitCanvas(canvas, context);
    });
    resizeObserver.observe(canvas);

    const render = (now: number) => {
      if (disposed) return;
      const delta = Math.min(48, now - previous);
      previous = now;
      const { width, height } = fitCanvas(canvas, context);
      const current = runtimeRef.current;

      if (theme === "star-voyage") {
        drawStarVoyage(context, width, height, tokens, stars, current, now, delta, reducedMotion);
      } else if (theme === "code-rain") {
        drawCodeRain(context, width, height, tokens, current, now, delta, reducedMotion);
      } else if (theme === "kinetic-cloud") {
        if (
          cloudLayout.length === 0 ||
          Math.abs(cloudLayoutWidth - width) > 2 ||
          Math.abs(cloudLayoutHeight - height) > 2
        ) {
          cloudLayout = makeCloudLayout(context, tokens, width, height);
          cloudLayoutWidth = width;
          cloudLayoutHeight = height;
        }
        drawKineticCloud(context, width, height, cloudLayout, current, now, reducedMotion);
      } else {
        drawImmersiveWheel(
          context,
          width,
          height,
          wheelEntries,
          current,
          wheelMotion,
          now,
          delta,
          reducedMotion,
        );
      }
      animationFrame = requestAnimationFrame(render);
    };

    animationFrame = requestAnimationFrame(render);
    return () => {
      disposed = true;
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, [reducedMotion, sampleKey, sampledEntries, theme, wheelEntries, wheelKey]);

  return (
    <canvas
      ref={canvasRef}
      className="stage-canvas"
      aria-hidden="true"
      data-theme={theme}
      data-visual-count={sampledEntries.length}
      data-wheel-count={wheelEntries.length}
    />
  );
}
